import type { MaybeRef, MaybeRefOrGetter } from "vue"
import type { Album, EventVault } from "@/models"
import { computed, toValue } from "vue"
import { SocialsBehavior, TaggingBehavior } from "@/types"
import { canShowFreemiumBanner, canUploadToGallery } from "@/utils"

export interface UseGalleryPermissionsOptions {
  isHost?: MaybeRefOrGetter<boolean | undefined>
}

/**
 * Determine the current access availability of a given event vault or album
 *
 * @param eventVault The event vault to determine gallery permissions against
 * @param album The album to determine gallery permissions against
 */
export function useGalleryPermissions(eventVault?: MaybeRef<EventVault | undefined>, album?: MaybeRef<Album | undefined>, options: UseGalleryPermissionsOptions = {}) {
  const currentEvent = computed(() => toValue(eventVault))
  const currentAlbum = computed(() => toValue(album))
  const isHost = computed(() => toValue(options.isHost) ?? false)

  const isGalleryEnabled = computed(() => {
    if (isHost.value) {
      return true
    }

    if (currentAlbum.value) {
      return currentAlbum.value?.settings.sharePix ?? false
    }

    if (!currentEvent.value?.eventVaultSettings?.sharePixWithGuests) {
      return false
    }

    return true
  })

  const isMainGalleryEnabled = computed(() => {
    if (isHost.value) {
      return true
    }

    if (currentEvent.value?.eventVaultSettings?.hideMainGallery) {
      return false
    }

    return true
  })

  const isUploadsEnabled = computed(() => {
    return canUploadToGallery(isHost.value, currentEvent.value, currentAlbum.value)
  })

  const isTaggingEnabled = computed(() => {
    return currentEvent.value?.eventVaultSettings?.taggingBehavior === TaggingBehavior.On
  })

  const isReactionsEnabled = computed(() => {
    const socialBehavior = currentEvent.value?.eventVaultSettings?.socialsBehavior ?? SocialsBehavior.Disabled
    return [SocialsBehavior.ReactionsOnly, SocialsBehavior.ReactionsAndComments].includes(socialBehavior)
  })

  const isVideoUploadsEnabled = computed(() => {
    return currentEvent.value?.eventVaultSettings?.videoEnabled && !currentEvent.value?.freemiumMode
  })

  const isGuestbookEnabled = computed(() => {
    return !currentEvent.value?.eventVaultSettings?.hideGuestbook || isHost.value
  })

  const isZipDownloadAllowed = computed(() => {
    return currentEvent.value?.eventVaultSettings?.allowGuestDownload || isHost.value
  })

  const isFreemiumEvent = computed(() => currentEvent.value?.freemiumMode ?? false)

  const showFreemiumBanner = computed(() => canShowFreemiumBanner(currentEvent.value))

  const isCurrentAlbumLocked = computed(() => {
    return currentAlbum.value
      && currentAlbum.value.isLocked
      && !isHost.value
  })

  return {
    isGalleryEnabled,
    isMainGalleryEnabled,
    isUploadsEnabled,
    isVideoUploadsEnabled,
    isGuestbookEnabled,
    isFreemiumEvent,
    showFreemiumBanner,
    isZipDownloadAllowed,
    isCurrentAlbumLocked,
    isTaggingEnabled,
    isReactionsEnabled,
  }
}
